import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { sanitizeReturnUrl } from '@core/utils/url-sanitizer';
import { Role } from 'portal-shared';
import { GlobalNotificationService } from '../services/global-notification.service';
import { HttpError } from '@core/errors/http-errors';

export const authGuard: CanMatchFn = async (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const notificationService = inject(GlobalNotificationService);

  const targetUrl =
    router.getCurrentNavigation()?.extractedUrl?.toString() ||
    router.url ||
    route.path ||
    '';
  const requiredRoles: ReadonlyArray<Role> =
    (route.data?.['requiredRoles'] as ReadonlyArray<Role>) ?? [];

  let userData;

  try {
    const isAuthorized = await authService.isUserAuthorized(
      [], // No longer using old Role system
      targetUrl,
    );

    if (!isAuthorized) {
      return router.createUrlTree(['/login'], {
        queryParams: {
          message: 'login-required',
          returnUrl: sanitizeReturnUrl(targetUrl),
        },
      });
    }

    // Get user data once for all subsequent checks
    userData = await authService.getUserData();

    if (!userData) {
      return router.createUrlTree(['/login'], {
        queryParams: {
          message: 'login-required',
          returnUrl: sanitizeReturnUrl(targetUrl),
        },
      });
    }
  } catch (error) {
    // Handle network errors (0) and service unavailable errors (503)
    // by showing a notification and staying on the current page
    if (
      error instanceof HttpError &&
      (error.status === 0 || error.status === 503)
    ) {
      notificationService.show({ type: 'error', title: error.message });
      // Return current URL to cancel navigation without routing error
      // If we're already on a page, stay there; if on login, stay on login
      return router.parseUrl(router.url || '/login');
    }
    // For other errors, let the default error handling take over
    throw error;
  }

  // Validate role - must be ADMIN or INFLUENCER, not NONE (empty string)
  // Role.NONE = '' (empty string), so !userData.role catches it
  // But explicit check for clarity and type safety
  if (userData.role === Role.NONE || !userData.role) {
    console.warn(`User ${userData.userId} does not have a valid role`);
    return router.createUrlTree(['/login'], {
      queryParams: {
        message: 'access-required',
        returnUrl: sanitizeReturnUrl(targetUrl),
      },
    });
  }

  // Check required roles for this route
  if (requiredRoles && requiredRoles.length > 0) {
    if (!requiredRoles.includes(userData.role)) {
      // User doesn't have required role - redirect based on their role
      if (userData.role === Role.INFLUENCER) {
        // Viewers trying to access admin routes go to vendor directory
        return router.createUrlTree(['/vendors']);
      }
      // Shouldn't happen, but redirect to login if neither ADMIN nor INFLUENCER
      return router.createUrlTree(['/login']);
    }
  }

  authService.listenForInactivity();
  return true;
};
